import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  Checkbox
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import fr from 'date-fns/locale/fr';
import { supabase } from '../../supabaseClient';
import { useMandates } from '../../contexts/MandateContext';
import { useSecteur } from '../../contexts/SecteurContext';

function AssignMandateForm({ selectedMember, onClose }) {
  const { refreshMandates } = useMandates();
  const { selectedSecteur } = useSecteur();
  const [formData, setFormData] = useState({
    userId: '',  // Initialiser avec une chaîne vide
    releveCodeIds: []
  });

  const [users, setUsers] = useState([]);
  const [releveCodes, setReleveCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showSectorUsers, setShowSectorUsers] = useState(true);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Récupérer tous les utilisateurs
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('*')
          .order('full_name');

        if (usersError) throw usersError;

        // Récupérer les utilisateurs qui ont des mandats actifs dans le secteur
        const { data: mandateUsersData, error: mandateError } = await supabase
          .from('user_mandates')
          .select('user_id')
          .eq('sector_id', selectedSecteur)
          .eq('is_active', true);

        if (mandateError) throw mandateError;

        // Créer un Set des IDs des utilisateurs avec mandats actifs
        const userIdsWithMandates = new Set(mandateUsersData.map(m => m.user_id));

        // Séparer les utilisateurs
        const usersWithMandates = [];
        const usersWithoutMandates = [];

        usersData.forEach(user => {
          if (userIdsWithMandates.has(user.id)) {
            usersWithMandates.push(user);
          } else {
            usersWithoutMandates.push(user);
          }
        });

        setAllUsers({
          withMandates: usersWithMandates,
          withoutMandates: usersWithoutMandates
        });

        // Initialiser users avec les utilisateurs du secteur par défaut
        setUsers(usersWithMandates);

        const [
          { data: releveCodesData, error: releveCodesError }
        ] = await Promise.all([
          supabase.from('releve_codes')
            .select('id, code, role, mandate_description, hours_per_month, particularity')
            .eq('is_active', true)
        ]);

        if (releveCodesError) throw releveCodesError;

        setReleveCodes(releveCodesData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        showSnackbar('Erreur lors du chargement des données', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedSecteur]);

  // Mettre à jour le formulaire quand selectedMember change
  useEffect(() => {
    if (selectedMember && selectedMember.id) {
      setFormData(prev => ({
        ...prev,
        userId: selectedMember.id
      }));

      // Charger les mandats existants pour cet utilisateur
      const loadExistingMandates = async () => {
        try {
          const { data: existingMandates, error: checkError } = await supabase
            .from('user_mandates')
            .select('releve_code_id')
            .eq('user_id', selectedMember.id)
            .eq('sector_id', selectedSecteur)
            .eq('is_active', true);

          if (checkError) throw checkError;

          setFormData(prev => ({
            ...prev,
            userId: selectedMember.id,
            releveCodeIds: existingMandates.map(m => m.releve_code_id)
          }));
        } catch (error) {
          console.error('Erreur lors du chargement des mandats existants:', error);
        }
      };

      loadExistingMandates();
    }
  }, [selectedMember, selectedSecteur]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));

    // Si c'est le champ utilisateur qui change, charger les mandats actifs
    if (name === 'userId') {
      try {
        const { data: existingMandates, error: checkError } = await supabase
          .from('user_mandates')
          .select('releve_code_id')
          .eq('user_id', value)
          .eq('sector_id', selectedSecteur)
          .eq('is_active', true);

        if (checkError) throw checkError;

        // Mettre à jour les codes de relève avec les mandats existants
        setFormData(prev => ({
          ...prev,
          userId: value,
          releveCodeIds: existingMandates.map(m => m.releve_code_id)
        }));
      } catch (error) {
        console.error('Erreur lors du chargement des mandats existants:', error);
        showSnackbar('Erreur lors du chargement des mandats existants', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.userId) {
      showSnackbar('Veuillez sélectionner un membre', 'error');
      return;
    }

    try {
      // 1. Vérifier si l'utilisateur a déjà le secteur
      const { data: userSector, error: sectorError } = await supabase
        .from('user_sectors')
        .select('*')
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur)
        .single();

      if (sectorError && sectorError.code !== 'PGRST116') { // PGRST116 = not found
        throw sectorError;
      }

      // Si l'utilisateur n'a pas le secteur et qu'il y a des mandats à attribuer
      if (!userSector && formData.releveCodeIds.length > 0) {
        const { error: insertSectorError } = await supabase
          .from('user_sectors')
          .insert({
            user_id: formData.userId,
            sector_id: selectedSecteur
          });

        if (insertSectorError) throw insertSectorError;
      }

      // 2. Gestion des mandats (code existant)
      const { data: existingMandates, error: checkError } = await supabase
        .from('user_mandates')
        .select('releve_code_id, is_active')
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur);

      if (checkError) throw checkError;

      // 2. Filtrer les différents cas

      const mandatesNeedingUpdate = existingMandates
        .filter(m => !m.is_active && formData.releveCodeIds.includes(m.releve_code_id))
        .map(m => m.releve_code_id);

      const newReleveCodeIds = formData.releveCodeIds.filter(
        id => !existingMandates.some(m => m.releve_code_id === id)
      );

      // 3. Identifier les mandats à désactiver (ceux qui sont actifs mais plus sélectionnés)
      const mandatesToDeactivate = existingMandates
        .filter(m => m.is_active && !formData.releveCodeIds.includes(m.releve_code_id))
        .map(m => m.releve_code_id);

      // 4. Désactiver les mandats non sélectionnés
      if (mandatesToDeactivate.length > 0) {
        const { error: deactivateError } = await supabase
          .from('user_mandates')
          .update({ is_active: false })
          .eq('user_id', formData.userId)
          .eq('sector_id', selectedSecteur)
          .in('releve_code_id', mandatesToDeactivate);

        if (deactivateError) throw deactivateError;
      }

      // 5. Réactiver les mandats inactifs si nécessaire
      if (mandatesNeedingUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('user_mandates')
          .update({ is_active: true })
          .eq('user_id', formData.userId)
          .eq('sector_id', selectedSecteur)
          .in('releve_code_id', mandatesNeedingUpdate);

        if (updateError) throw updateError;
      }

      // 6. Créer les nouveaux mandats
      if (newReleveCodeIds.length > 0) {
        const mandatesToCreate = newReleveCodeIds.map(releveCodeId => ({
          user_id: formData.userId,
          releve_code_id: releveCodeId,
          sector_id: selectedSecteur,
          is_active: true
        }));

        const { error: insertError } = await supabase
          .from('user_mandates')
          .insert(mandatesToCreate);

        if (insertError) throw insertError;
      }

      // 3. Après avoir géré les mandats, vérifier s'il reste des mandats actifs
      const { data: activeMandates, error: activeCheckError } = await supabase
        .from('user_mandates')
        .select('id')
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (activeCheckError) throw activeCheckError;

      // Si l'utilisateur n'a plus de mandats actifs, retirer le secteur
      if (activeMandates.length === 0 && userSector) {
        const { error: deleteSectorError } = await supabase
          .from('user_sectors')
          .delete()
          .eq('user_id', formData.userId)
          .eq('sector_id', selectedSecteur);

        if (deleteSectorError) throw deleteSectorError;
      }

      showSnackbar('Mandats attribués avec succès', 'success');
      refreshMandates();
    } catch (error) {
      console.error('Erreur lors de l\'attribution des mandats:', error);
      showSnackbar(`Erreur : ${error.message}`, 'error');
    }
  };

  const handleDeactivateAllMandates = async () => {
    if (!formData.userId) {
      showSnackbar('Veuillez sélectionner un membre', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('user_mandates')
        .update({ is_active: false })
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur);

      if (error) throw error;

      // Retirer le secteur car plus aucun mandat actif
      const { error: deleteSectorError } = await supabase
        .from('user_sectors')
        .delete()
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur);

      if (deleteSectorError) throw deleteSectorError;

      showSnackbar('Tous les moyens ont été désactivés', 'success');
      refreshMandates();
      setFormData(prev => ({
        ...prev,
        releveCodeIds: []
      }));
    } catch (error) {
      console.error('Erreur lors de la désactivation des mandats:', error);
      showSnackbar('Erreur lors de la désactivation des mandats', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle sector switch change
  const handleSectorSwitchChange = (event) => {
    const showSectorUsers = event.target.checked;
    setShowSectorUsers(showSectorUsers);
    setUsers(showSectorUsers ? allUsers.withMandates : allUsers.withoutMandates);
    // Reset user selection when switching
    setFormData(prev => ({ ...prev, userId: '' }));
  };

  const getTextColorForBackground = (backgroundColor) => {
    if (!backgroundColor) return '#000';
    const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const uicolors = [r / 255, g / 255, b / 255];
    const c = uicolors.map((col) => {
      if (col <= 0.03928) {
        return col / 12.92;
      }
      return Math.pow((col + 0.055) / 1.055, 2.4);
    });
    const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    return L > 0.179 ? '#000' : '#fff';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Membre</InputLabel>
                <Select
                  name="userId"
                  value={formData.userId || ''}  // S'assurer qu'une valeur vide est fournie si undefined
                  onChange={handleChange}
                  required
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSectorUsers}
                    onChange={handleSectorSwitchChange}
                    color="primary"
                  />
                }
                label="Du secteur"
                sx={{ minWidth: '140px' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Codes de relève</InputLabel>
                <Select
                  multiple
                  name="releveCodeIds"
                  value={formData.releveCodeIds}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    releveCodeIds: e.target.value
                  }))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const code = releveCodes.find(c => c.id === value);
                        return (
                          <Chip
                            key={value}
                            label={`${code?.code}${code?.role ? ` (${code.role})` : ''}`}
                            sx={{
                              backgroundColor: code?.color,
                              '& .MuiChip-label': {
                                color: getTextColorForBackground(code?.color)
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {releveCodes.map((code) => (
                    <MenuItem key={code.id} value={code.id}>
                      <Checkbox checked={formData.releveCodeIds.includes(code.id)} />
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: code.color,
                          color: getTextColorForBackground(code.color),
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          minWidth: '100px'
                        }}
                      >
                        {code.code}
                      </Box>
                      <Typography sx={{ ml: 1 }}>
                        {code.role ? `(${code.role})` : ''}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, minWidth: 'fit-content' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Attribuer
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeactivateAllMandates}
                >
                  Désactiver
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
}

export default AssignMandateForm;
